/**
 * RsDataPlotPage Component
 *
 * Full-page view of a single routine surveillance scatter plot for one country
 * and one metric. Reached via the expand button on a small-multiple plot in
 * RsDataPlots. The route encodes level/country/metric as path segments:
 *   /data/rs/:level/full-page-plot/:country/:metric
 *
 * It re-fetches exactly that country+metric's national (ADMIN0) series and
 * renders it large. Being URL-driven, the page is shareable and works when
 * opened directly in a new tab.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { ArrowLeft, MousePointerClick } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/hooks/useAuth"
import { getIso3ToCountryMap } from "@/services/mapAdminInfoService"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import ScatterPlot, { ScatterPoint } from "@/components/rs-data/ScatterPlot"
import DotSizeLegend from "@/components/rs-data/DotSizeLegend"
import { RS_COLUMNS } from "@/components/rs-data/RsDataTable"
import {
  fetchCountryMetricRows,
  fetchAdminUnitMetricRows,
  rowToScatterPoint,
  parseRowDate,
  computeDaySpan,
  computeCategoryDifferences,
  computeAtypicalValues,
  computeCategoryCombinations,
} from "@/components/rs-data/rsPlotData"
import CategoryDifferencesNote from "@/components/rs-data/CategoryDifferencesNote"
import AtypicalValuesNote from "@/components/rs-data/AtypicalValuesNote"
import CategoriesOkNote from "@/components/rs-data/CategoriesOkNote"
import {
  DEFAULT_TOPIC,
  RoutineSurveillanceRow,
  RoutineSurveillanceTopic,
} from "@/services/routineSurveillanceService"

/** Rendered height of the full-page plot in pixels. Larger than the grid cells. */
const FULL_PLOT_HEIGHT: number = 520

/**
 * viewBox width for the full-page plot. The SVG scales to the container width,
 * so this only sets the width-to-height proportion; a wide value (paired with
 * FULL_PLOT_HEIGHT) gives a landscape plot that fills the page width instead of
 * the narrow, tall default suited to grid cells.
 */
const FULL_PLOT_VIEWBOX_WIDTH: number = 1400

/**
 * Dot-radius multiplier for the full-page plot. The base radii are tuned for
 * small grid cells; the full-page plot has far more room, so its dots are
 * enlarged to stay legible at this scale.
 */
const FULL_PLOT_DOT_RADIUS_SCALE: number = 2.2

/**
 * Categorical colour palette used to colour dots by their category combination.
 * A visually distinct, qualitative set (not the app's monochrome amber --chart-*
 * ramp, which is a sequential scale unsuited to telling categories apart). Group
 * colours are assigned by index in first-seen order; if there are more groups
 * than colours the palette wraps (see colourForCombinationIndex).
 */
const CATEGORY_COMBINATION_COLORS: readonly string[] = [
  "oklch(0.62 0.20 25)",   // red
  "oklch(0.68 0.17 145)",  // green
  "oklch(0.60 0.18 265)",  // blue
  "oklch(0.72 0.17 60)",   // orange
  "oklch(0.60 0.20 320)",  // magenta
  "oklch(0.68 0.13 195)",  // teal
  "oklch(0.70 0.17 100)",  // yellow-green
  "oklch(0.55 0.16 300)",  // purple
] as const

/**
 * Resolve the dot colour for a category-combination group by its index in the
 * first-seen order, wrapping the palette when there are more groups than
 * colours so every group always gets a colour.
 *
 * @param index - The group's index in the combinations list.
 * @returns A CSS colour string from the palette.
 */
function colourForCombinationIndex(index: number): string {
  return CATEGORY_COMBINATION_COLORS[index % CATEGORY_COMBINATION_COLORS.length]
}

/**
 * Full-page single scatter plot for one country + metric.
 *
 * Reads the country and metric from the path segments (level comes from the
 * route via the `level` prop), resolves the country's ADMIN0 id and display
 * name, fetches its national rows for the metric, and renders a large
 * ScatterPlot. The day-span filter is persisted as minDays/maxDays query params.
 * Missing/invalid params and fetch failures are surfaced explicitly rather than
 * silently rendering an empty plot.
 *
 * @param props - Component props.
 * @param props.level - Which surveillance level the plot belongs to; supplied
 *        by the route. Used for the back link. Defaults to "national".
 * @returns The full-page graph, or an appropriate loading/error/empty state.
 */
export default function RsDataPlotPage({
  level = "national",
}: {
  level?: "national" | "subnational"
}): React.JSX.Element {
  const { getAccessToken } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const params = useParams<{
    country?: string
    // Subnational-only path segments (from the subnational full-page-plot
    // route). Absent on the national route.
    adminLevel?: string
    adminUnit?: string
    metric?: string
  }>()

  const isSubnational: boolean = level === "subnational"

  // The plot's identity comes from the path; the topic and the day-span filter
  // stay query params (topic / minDays / maxDays). National is identified by
  // country + metric; subnational adds admin level + admin unit (map_admin_id).
  const iso3: string = params.country ?? ""
  const metric: string = params.metric ?? ""
  const adminLevel: string = isSubnational ? (params.adminLevel ?? "") : ""
  const mapAdminId: string = isSubnational ? (params.adminUnit ?? "") : ""

  // The topic partition, read from ?topic (defaults to "incidence" when absent
  // or unrecognised) so this shareable page re-fetches the same topic's series
  // the user expanded from.
  const rawTopic: string | null = searchParams.get("topic")
  const topic: RoutineSurveillanceTopic =
    rawTopic === "incidence" || rawTopic === "mortality" ? rawTopic : DEFAULT_TOPIC

  const [countryName, setCountryName] = useState<string>(iso3)
  // (Subnational) The admin unit's display name, resolved from the fetched
  // rows (which carry admin_unit_name). Falls back to the id until resolved.
  const [adminUnitName, setAdminUnitName] = useState<string>(mapAdminId)
  // Full unprojected rows for the data table (every column). Plot points are
  // derived from these (below) so a point and its table row share an identity.
  const [rows, setRows] = useState<RoutineSurveillanceRow[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // The selected row's index (into `rows`), or null. Shared identity linking a
  // plotted dot to its table row: clicking either selects the same index.
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null)

  // The selected [min, max] day-span range for the filter slider. Filters BOTH
  // the plotted dots and the table rows so the two stay consistent. Reset to the
  // full extent ([0, maxDaySpan]) whenever the data changes (see effect below).
  const [daySpanRange, setDaySpanRange] = useState<[number, number]>([0, 0])

  // Refs to each rendered table row, keyed by row index, so selecting a dot can
  // scroll the matching row into view within the table's scroll container.
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map())

  // Guards the mount fetch against double-invocation (StrictMode) while still
  // re-running when the query params change.
  const fetchIdRef = useRef<number>(0)

  // Title reads "<place> — <metric>". On subnational the place is the admin
  // unit within its country; on national it is the country.
  const pageTitle: string =
    metric !== "" && iso3 !== ""
      ? isSubnational
        ? `${adminUnitName} (${countryName}) — ${metric}`
        : `${countryName} — ${metric}`
      : "Surveillance Graph"

  /**
   * Resolve the country's ADMIN0 id and name, then fetch and parse its national
   * scatter series for the metric.
   */
  const loadData = useCallback(async (): Promise<void> => {
    // The required params to identify a plot differ by level; bail loudly rather
    // than fetching a meaningless query. National: country + metric. Subnational
    // additionally needs the admin level + admin unit (map_admin_id).
    if (iso3 === "" || metric === "") {
      setError("This graph link is missing its country or metric.")
      setLoading(false)
      return
    }
    if (isSubnational && (adminLevel === "" || mapAdminId === "")) {
      setError("This graph link is missing its admin level or admin unit.")
      setLoading(false)
      return
    }

    const currentFetchId: number = ++fetchIdRef.current
    setLoading(true)
    setError(null)

    try {
      const token = await getAccessToken()

      // Resolve the country name for the title. Rows are selected by
      // iso3 + admin_level (+ map_admin_id on subnational) directly, so no
      // ADMIN0 id lookup is needed.
      const iso3ToCountry = await getIso3ToCountryMap()

      if (currentFetchId !== fetchIdRef.current) return

      setCountryName(iso3ToCountry.get(iso3) ?? iso3)

      // Fetch the rows for this plot, branching on level. Subnational scopes to
      // a single admin unit; national to the country aggregate.
      const result = isSubnational
        ? await fetchAdminUnitMetricRows(
            token,
            iso3,
            adminLevel,
            mapAdminId,
            metric,
            topic
          )
        : await fetchCountryMetricRows(token, iso3, metric, topic)

      if (currentFetchId !== fetchIdRef.current) return

      // Surface truncation rather than plotting/tabulating a partial set.
      if (result.truncated) {
        setError(
          isSubnational
            ? "Too many data points to display for this admin unit and metric."
            : "Too many data points to display for this country and metric."
        )
        setRows([])
        setSelectedRowIndex(null)
        return
      }

      // (Subnational) Resolve the admin unit's display name from the fetched
      // rows for the title; fall back to the id if no row carries a name.
      if (isSubnational) {
        const namedRow = result.rows.find(
          (row) => row.admin_unit_name !== null && row.admin_unit_name !== ""
        )
        setAdminUnitName(namedRow?.admin_unit_name ?? mapAdminId)
      }

      setRows(result.rows)
      setSelectedRowIndex(null)
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return
      setError(err instanceof Error ? err.message : "Failed to load graph data")
      setRows([])
      setSelectedRowIndex(null)
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false)
      }
    }
  }, [getAccessToken, iso3, metric, topic, isSubnational, adminLevel, mapAdminId])

  useEffect(() => {
    void loadData()
  }, [loadData])

  // The back link returns to the graph view for the same level so the user
  // lands where the expand button was, not on a generic page.
  // Preserve the selected country/metric so Back returns to the same plots
  // view the user expanded from, not a fresh page. Both segments are encoded
  // since metric names may contain spaces/slashes.
  // Preserve the topic on the way back so the plots view reopens in the same
  // topic. Only appended for the non-default topic, matching how the plots view
  // keeps the URL clean for the default ("incidence").
  const topicQuery: string =
    topic !== DEFAULT_TOPIC ? `?topic=${encodeURIComponent(topic)}` : ""
  // Rebuild the plots-view path the expand came from, per level. National:
  // country/metric. Subnational: country/adminLevel/adminUnit/metric — landing
  // back on the specific admin unit (per-metric) view the user expanded from.
  let backTo: string
  if (isSubnational) {
    backTo =
      iso3 !== "" && adminLevel !== "" && mapAdminId !== ""
        ? `/data/rs/subnational/plots/${encodeURIComponent(iso3)}/` +
          `${encodeURIComponent(adminLevel)}/${encodeURIComponent(mapAdminId)}${topicQuery}`
        : `/data/rs/subnational/plots${topicQuery}`
  } else {
    backTo =
      iso3 !== "" && metric !== ""
        ? `/data/rs/national/plots/${encodeURIComponent(iso3)}/${encodeURIComponent(metric)}${topicQuery}`
        : `/data/rs/national/plots${topicQuery}`
  }

  // The day span (end_date - start_date, whole days) for each row, keyed by its
  // original index. null when it can't be computed. Drives both the slider's max
  // and the day-span filter applied to the plot and table.
  const daySpanByIndex: Map<number, number | null> = useMemo(() => {
    const map = new Map<number, number | null>()
    rows.forEach((row, index) => {
      const start: Date | null = parseRowDate(row.start_date)
      map.set(index, start !== null ? computeDaySpan(start, row.end_date) : null)
    })
    return map
  }, [rows])

  // The largest day span across all rows on the page — the slider's max.
  const maxDaySpan: number = useMemo(() => {
    let maxSpan = 0
    for (const span of daySpanByIndex.values()) {
      if (span !== null && span > maxSpan) {
        maxSpan = span
      }
    }
    return maxSpan
  }, [daySpanByIndex])

  // The smallest day span across all rows on the page — the slider's min. Null
  // when no row has a day span. Computed separately so the slider spans the
  // actual data range rather than starting at a hardcoded 0.
  const minDaySpan: number | null = useMemo(() => {
    let minSpan: number | null = null
    for (const span of daySpanByIndex.values()) {
      if (span !== null && (minSpan === null || span < minSpan)) {
        minSpan = span
      }
    }
    return minSpan
  }, [daySpanByIndex])

  // Seed the day-span filter whenever the data changes. If the URL carries an
  // explicit minDays/maxDays range (a user-shared/narrowed view), honour it,
  // clamped to the data's actual extent; otherwise reset to the full extent so
  // a freshly loaded plot shows every dot/row.
  useEffect(() => {
    const dataMin: number = minDaySpan ?? 0
    const dataMax: number = maxDaySpan

    const rawMin: string | null = searchParams.get("minDays")
    const rawMax: string | null = searchParams.get("maxDays")
    const parsedMin: number = Number(rawMin)
    const parsedMax: number = Number(rawMax)

    // Only apply URL values when both are present and numeric; otherwise use the
    // data's full extent.
    if (rawMin !== null && rawMax !== null && !Number.isNaN(parsedMin) && !Number.isNaN(parsedMax)) {
      const clampedMin: number = Math.max(dataMin, Math.min(parsedMin, dataMax))
      const clampedMax: number = Math.max(clampedMin, Math.min(parsedMax, dataMax))
      setDaySpanRange([clampedMin, clampedMax])
    } else {
      setDaySpanRange([dataMin, dataMax])
    }
    // Note: seeded only when the DATA (min/max) changes, not on every param
    // change — the slider handler owns writes back to the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDaySpan, maxDaySpan])

  // Whether the slider is at its full extent (no filtering). At full extent,
  // rows with an unknown day span are kept; narrowing either end hides them,
  // since an unknown span can't be placed within a bounded range.
  const isFullDaySpanRange: boolean =
    daySpanRange[0] <= (minDaySpan ?? 0) && daySpanRange[1] >= maxDaySpan

  /**
   * Whether a given original row index passes the current day-span filter.
   * Shared by the plot and table so both show exactly the same subset.
   *
   * @param index - The original row index to test.
   * @returns True when the row's day span is within the selected range.
   */
  const passesDaySpanFilter = useCallback((index: number): boolean => {
    if (isFullDaySpanRange) {
      return true
    }
    const span: number | null | undefined = daySpanByIndex.get(index)
    // Unknown span: only shown at full extent (handled above); excluded once
    // the range is narrowed.
    if (span === null || span === undefined) {
      return false
    }
    return span >= daySpanRange[0] && span <= daySpanRange[1]
  }, [isFullDaySpanRange, daySpanByIndex, daySpanRange])

  /**
   * Handle a slider change: update the range state and reflect it in the URL.
   *
   * The minDays/maxDays query params are only written when the user has
   * actually narrowed the range (it differs from the data's full extent);
   * selecting the full extent removes them, keeping the URL clean and matching
   * the "no filter" default. Other query params (iso3/metric/level) are
   * preserved.
   *
   * @param next - The slider's new value (a two-element [min, max] array).
   */
  const handleDaySpanChange = useCallback((next: number[]): void => {
    if (next.length !== 2) return
    const [nextMin, nextMax] = next
    setDaySpanRange([nextMin, nextMax])

    const dataMin: number = minDaySpan ?? 0
    const dataMax: number = maxDaySpan
    const isFullExtent: boolean = nextMin <= dataMin && nextMax >= dataMax

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (isFullExtent) {
          // Full extent == no filter: keep the URL clean.
          params.delete("minDays")
          params.delete("maxDays")
        } else {
          params.set("minDays", String(nextMin))
          params.set("maxDays", String(nextMax))
        }
        return params
      },
      { replace: true }
    )
  }, [minDaySpan, maxDaySpan, setSearchParams])

  // Colour each visible row by its category combination. Groups are computed
  // from exactly the rows the plot/table show (after the day-span filter), and
  // only the category columns that DIFFER across them drive the grouping — so a
  // uniform plot is a single group/colour, and default/typical values collapse
  // together (see computeCategoryCombinations).
  const { colourByOriginalIndex, labelByOriginalIndex, legendEntries } = useMemo(() => {
    // Work over the visible rows only, keeping each row's ORIGINAL index so the
    // resulting colour/label can be looked up when building points (which use
    // the original index as their id).
    const visible: { row: RoutineSurveillanceRow; originalIndex: number }[] = []
    rows.forEach((row, originalIndex) => {
      if (passesDaySpanFilter(originalIndex)) {
        visible.push({ row, originalIndex })
      }
    })

    const visibleRows: RoutineSurveillanceRow[] = visible.map(({ row }) => row)
    const { combinations, idByRowIndex } = computeCategoryCombinations(visibleRows)

    // Whether applying custom category colours is meaningful at all. Two cases
    // warrant it: DIVERSE categories (more than one group), or UNIFORM-but-
    // ATYPICAL (a single group whose shared values are non-default). When the
    // rows are uniform AND typical, colouring conveys nothing — the dots keep
    // ScatterPlot's default colour (matching the small multiples), and no
    // legend is shown.
    const isDiverse: boolean = combinations.length > 1
    const isUniformAtypical: boolean =
      combinations.length <= 1 &&
      computeCategoryDifferences(visibleRows).length === 0 &&
      computeAtypicalValues(visibleRows).length > 0
    const colouringIsMeaningful: boolean = isDiverse || isUniformAtypical

    // Assign a palette colour to each group by its first-seen order, and index
    // each group's label by its id for the per-point tooltip.
    const colourByGroupId: Map<string, string> = new Map()
    const labelByGroupId: Map<string, string> = new Map()
    combinations.forEach((combination, index) => {
      colourByGroupId.set(combination.id, colourForCombinationIndex(index))
      labelByGroupId.set(combination.id, combination.label)
    })

    // Only surface a per-point label when the grouping actually distinguishes
    // dots (more than one group). With a single group the label ("All records"
    // / "Typical") adds noise to every tooltip without conveying anything.
    const labelsAreMeaningful: boolean = combinations.length > 1

    // Map each visible row's ORIGINAL index to its group colour and label. When
    // colouring is not meaningful (uniform + typical) leave the colour map empty
    // so dots fall back to ScatterPlot's default colour.
    const colourMap: Map<number, string> = new Map()
    const labelMap: Map<number, string> = new Map()
    visible.forEach(({ originalIndex }, visibleIndex) => {
      const groupId: string = idByRowIndex[visibleIndex]
      if (colouringIsMeaningful) {
        const colour: string | undefined = colourByGroupId.get(groupId)
        if (colour !== undefined) {
          colourMap.set(originalIndex, colour)
        }
      }
      const label: string | undefined = labelByGroupId.get(groupId)
      if (labelsAreMeaningful && label !== undefined) {
        labelMap.set(originalIndex, label)
      }
    })

    // Legend entries in first-seen (colour-assignment) order. Empty when
    // colouring is not meaningful, so the uniform-typical case shows no legend.
    const legend: { colour: string; label: string }[] = colouringIsMeaningful
      ? combinations.map((combination, index) => ({
          colour: colourForCombinationIndex(index),
          label: combination.label,
        }))
      : []

    return {
      colourByOriginalIndex: colourMap,
      labelByOriginalIndex: labelMap,
      legendEntries: legend,
    }
  }, [rows, passesDaySpanFilter])

  // Plot points derived from the rows, each tagged with its ORIGINAL row index
  // as the id and its category-combination colour, then filtered by the
  // day-span range. Rows that can't be plotted (no date/value) are skipped, but
  // the id still refers to the correct row so dot ↔ row selection stays exact
  // regardless of table sort order.
  const points: ScatterPoint[] = useMemo(() => {
    const result: ScatterPoint[] = []
    rows.forEach((row, index) => {
      if (!passesDaySpanFilter(index)) return
      const point: ScatterPoint | null = rowToScatterPoint(row)
      if (point !== null) {
        // Colour the dot by its category group; undefined falls back to the
        // plot-wide default colour inside ScatterPlot. The label surfaces the
        // group's non-default columns in the hover tooltip.
        const colour: string | undefined = colourByOriginalIndex.get(index)
        const label: string | undefined = labelByOriginalIndex.get(index)
        result.push({
          ...point,
          id: index,
          ...(colour !== undefined ? { color: colour } : {}),
          ...(label !== undefined ? { label } : {}),
        })
      }
    })
    return result
  }, [rows, passesDaySpanFilter, colourByOriginalIndex, labelByOriginalIndex])

  // The table shows the rows behind the plot, filtered by the same day-span
  // range and sorted chronologically by start_date. Each entry keeps its
  // ORIGINAL index so a dot's id maps to the right row after sorting. Rows with
  // an unparseable/missing start_date sort last so they don't disrupt ordering.
  const tableRows: { row: RoutineSurveillanceRow; originalIndex: number }[] = useMemo(() => {
    const startTime = (row: RoutineSurveillanceRow): number => {
      if (row.start_date === null || row.start_date.trim() === "") {
        return Number.POSITIVE_INFINITY
      }
      const t: number = new Date(row.start_date).getTime()
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
    }
    return rows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ originalIndex }) => passesDaySpanFilter(originalIndex))
      .sort((a, b) => startTime(a.row) - startTime(b.row))
  }, [rows, passesDaySpanFilter])

  // Category-column analysis for the currently visible rows (same subset shown
  // in the plot/table after the day-span filter). If any column differs, we warn
  // about that; otherwise (uniform) we surface any non-default shared values.
  // The two are mutually exclusive.
  const { categoryDifferences, atypicalValues } = useMemo(() => {
    const visibleRows = tableRows.map(({ row }) => row)
    const differences = computeCategoryDifferences(visibleRows)
    return {
      categoryDifferences: differences,
      // Only compute atypical values when the rows are uniform — otherwise the
      // "differences" note takes precedence and this is unused.
      atypicalValues: differences.length === 0 ? computeAtypicalValues(visibleRows) : [],
    }
  }, [tableRows])

  // The legend actually rendered under the plot. Two cases:
  //  - Diverse categories (more than one group): show one entry per group,
  //    labelled by its differing columns (the usual colour key).
  //  - Uniform but ATYPICAL (a single group whose shared values are non-default):
  //    there is only one colour, but it is still worth a legend — relabel that
  //    single entry to list the atypical categories so the user sees WHICH
  //    non-default values every dot carries.
  // Otherwise (uniform and typical) there is nothing worth a legend.
  const displayLegendEntries: { colour: string; label: string }[] = useMemo(() => {
    // Diverse: the per-group legend already carries the meaningful labels.
    if (legendEntries.length > 1) {
      return legendEntries
    }
    // Uniform but atypical: relabel the single group with its atypical values.
    if (legendEntries.length === 1 && atypicalValues.length > 0) {
      const atypicalLabel: string = atypicalValues
        .map(({ label, value }) => `${label}: ${value}`)
        .join(", ")
      return [{ colour: legendEntries[0].colour, label: atypicalLabel }]
    }
    // Uniform and typical (or no data): no legend.
    return []
  }, [legendEntries, atypicalValues])

  /**
   * Select a row by its original index. Called both by clicking a table row and
   * (via the plot's onSelect) by clicking a dot, so the two views stay in sync.
   *
   * @param index - The original row index to select, or null to clear.
   */
  const handleSelect = useCallback((index: number | null): void => {
    setSelectedRowIndex(index)
  }, [])

  // When the selection changes (typically from a dot click), scroll the matching
  // table row into view so the user sees the highlight without hunting for it.
  useEffect(() => {
    if (selectedRowIndex === null) return
    const rowEl: HTMLTableRowElement | undefined = rowRefs.current.get(selectedRowIndex)
    // "nearest" avoids yanking the whole page; it only scrolls the table's
    // own scroll container when the row is off-screen within it.
    rowEl?.scrollIntoView({ block: "nearest" })
  }, [selectedRowIndex])

  // Clear the selection if the day-span filter hides the selected row, so a
  // no-longer-visible row isn't left marked as selected.
  useEffect(() => {
    if (selectedRowIndex !== null && !passesDaySpanFilter(selectedRowIndex)) {
      setSelectedRowIndex(null)
    }
  }, [selectedRowIndex, passesDaySpanFilter])

  return (
    <>
      <title>{pageTitle} - MAP Apps Platform</title>
      <div className="flex flex-1 flex-col min-h-0 overflow-y-auto p-6 gap-6">
        {/* Header: back button + title. */}
        <div className="flex items-center gap-4 flex-wrap">
          <Button asChild variant="ghost" size="sm">
            <Link to={backTo}>
              <ArrowLeft className="size-4 mr-1" />
              Back
            </Link>
          </Button>
          <h1 className="text-xl font-semibold">{pageTitle}</h1>
        </div>

        {/* Body: loading / error / empty / plot. */}
        {loading ? (
          <LoadingSpinner loadingContent="Loading graph data..." />
        ) : error !== null ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8">
            <p className="text-destructive">{error}</p>
            <button
              onClick={() => void loadData()}
              className="text-sm underline text-primary hover:text-primary/80"
            >
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center text-muted-foreground py-8">
            No data available for this country and metric.
          </div>
        ) : (
          <>
            {/* Controls row: interaction hint on the left, day-span filter
                pinned to the far right. */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* Interaction hint: the plot and table are linked by selection. */}
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <MousePointerClick className="size-4 shrink-0" aria-hidden="true" />
                Click a dot or a table row to highlight the matching one in the other.
              </p>

              {/* Dot-size legend: explains that dot size encodes the number of
                  days a point covers, drawing the min/max reference dots at this
                  plot's enlarged radius scale. Pushed right to group with the
                  day-span filter; shown under the same condition. */}
              {minDaySpan !== null && minDaySpan < maxDaySpan && (
                <div className="ml-auto">
                  <DotSizeLegend
                    minDaySpan={minDaySpan}
                    maxDaySpan={maxDaySpan}
                    dotRadiusScale={FULL_PLOT_DOT_RADIUS_SCALE}
                  />
                </div>
              )}

              {/* Day-span range filter. Filters BOTH the dots and the table rows
                  so the linked plot/table stay consistent. Shown once data with a
                  positive max span is loaded. */}
              {minDaySpan !== null && minDaySpan < maxDaySpan && (
                <div className="flex items-center gap-3">
                  <label
                    htmlFor="rs-graphpage-dayspan-slider"
                    className="text-sm text-muted-foreground whitespace-nowrap"
                  >
                    Days covered
                  </label>
                  <Slider
                    id="rs-graphpage-dayspan-slider"
                    className="w-[260px]"
                    min={minDaySpan}
                    max={maxDaySpan}
                    step={1}
                    value={daySpanRange}
                    onValueChange={handleDaySpanChange}
                    aria-label="Filter data by day span"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap tabular-nums">
                    {daySpanRange[0].toLocaleString()}–{daySpanRange[1].toLocaleString()} days
                  </span>
                </div>
              )}
            </div>

            <div className="w-full border border-border/50 rounded-md p-4">
              <ScatterPlot
                points={points}
                height={FULL_PLOT_HEIGHT}
                viewBoxWidth={FULL_PLOT_VIEWBOX_WIDTH}
                dotRadiusScale={FULL_PLOT_DOT_RADIUS_SCALE}
                selectedId={selectedRowIndex}
                onSelect={handleSelect}
              />

              {/* Colour legend. Shown when the categories are diverse (one entry
                  per group, labelled by the differing columns) OR when the rows
                  are uniform but atypical (a single entry listing the non-default
                  values every dot shares). Hidden when uniform and typical. */}
              {displayLegendEntries.length > 0 && (
                <ul
                  className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5"
                  aria-label="Category combination colour key"
                >
                  {displayLegendEntries.map(({ colour, label }) => (
                    <li
                      key={label}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <span
                        className="size-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: colour }}
                        aria-hidden="true"
                      />
                      {label}
                    </li>
                  ))}
                </ul>
              )}
              {/* Note, one of three states: warn if any category column differs;
                  else (uniform) surface non-default values; else an all-clear
                  tick. Mutually exclusive. */}
              <div className="mt-2">
                {categoryDifferences.length > 0 ? (
                  <CategoryDifferencesNote differingLabels={categoryDifferences} />
                ) : atypicalValues.length > 0 ? (
                  <AtypicalValuesNote atypicalValues={atypicalValues} />
                ) : tableRows.length > 0 ? (
                  // Only show the all-clear tick when there are actually visible
                  // rows — an empty (fully filtered out) plot isn't "uniform".
                  <CategoriesOkNote />
                ) : null}
              </div>
            </div>

            {/* Data table: the full rows behind the plot, every column, in the
                same column set/order as the main RS table (RS_COLUMNS). */}
            <div className="w-full">
              <h2 className="text-sm font-medium mb-2">
                Data ({tableRows.length.toLocaleString()} row
                {tableRows.length === 1 ? "" : "s"})
              </h2>
              <div className="border border-border/50 rounded-md overflow-auto max-h-[420px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {RS_COLUMNS.map(col => (
                        <TableHead
                          key={col.key}
                          className="text-xs sticky top-0 z-10 bg-card shadow-sm"
                        >
                          {col.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableRows.map(({ row, originalIndex }) => {
                      const isSelected: boolean = originalIndex === selectedRowIndex
                      return (
                        <TableRow
                          key={originalIndex}
                          ref={(el) => {
                            // Track the row element for scroll-into-view on dot
                            // selection; drop the entry when the row unmounts.
                            if (el) {
                              rowRefs.current.set(originalIndex, el)
                            } else {
                              rowRefs.current.delete(originalIndex)
                            }
                          }}
                          onClick={() => handleSelect(originalIndex)}
                          aria-selected={isSelected}
                          className={
                            "cursor-pointer transition-colors " +
                            (isSelected
                              ? "bg-primary/15 hover:bg-primary/20"
                              : "hover:bg-accent/30")
                          }
                        >
                          {RS_COLUMNS.map(col => (
                            <TableCell key={col.key} className="text-xs max-w-[220px]">
                              {col.key == "biblio_id" ? (
                                <Link to={`/all-sources/${row[col.key]}`} target="_blank">
                                  {row[col.key]}
                                </Link>
                              ) : (
                                <TruncatedCell value={row[col.key]} />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
